import { defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapState } from "pinia";
import { useApp } from "../../App/useApp";
import moment from "moment";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

import { IMultiSelectDropDownOption } from "../../../resources-vue/vue-interfaces/i-grouped-multiselect";
import { IGetAgency } from "../../../services/agency.service";
import { ApplicationEnum, ApplicationNameEnum } from "../../../enums/ApplicationEnum";
import config from "../../../common/config";
import { PermissionManager } from "../../../common/utilities/permission-manager";
import { ParentPermissionEnum } from "../../../enums/ParentPermissionEnum";
import { PermissionActionEnum } from "../../../enums/PermissionActionEnum";
import { HCTaskStatus, HHTaskStatus, HosPalTaskStatus } from "../../../enums/enums";
import { TaskReportStatus } from "../../../enums/TaskStatusEnum";
import { IEmployeeFilterList, UserService } from "../../../services/user-service";
import { DownloadService } from "../../../services/download.service";
import { ToastrService } from "../../../services/toastr.service";
import { IPatientCalendarRequestParams } from "../../../services/patient.service";
import { TaskService } from "../../../services/task.service";
import { ReportService } from "../../../services/report-service";
import { getTaskStatus } from "../../../common/vue-helpers/task-status";
import { AnalyticsCategory, AnalyticsService } from "../../../services/analytics.service";

import MultiSelect from "../../../resources-vue/vue-elements/MultiSelect/MultiSelect.vue";
import BranchDefaultFilter from "../../../resources-vue/vue-custom-elements/BranchDefaultFilter/BranchDefaultFilter.vue";
import DatePicker from "../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import MultiSelectFilter from "../../../resources-vue/vue-elements/MultiSelectFilter/MultiSelectFilter.vue";

export default defineComponent({
    components: {
        MultiSelect,
        BranchDefaultFilter,
        DatePicker,
        MultiSelectFilter
    },
    data(vm) {
        return {
            _analyticsService: null as AnalyticsService,
            _toastrService: null as ToastrService,
            _downloadService: null as DownloadService,
            _permissionManager: null as PermissionManager,
            _taskService: null as TaskService,
            _reportService: null as ReportService,
            organizationId: "",
            agencyList: [] as IGetAgency[],
            patientScheduleDetails: {
                viewType: null as string,
                selectedAgencies: [] as string[],
                selectedBranches: [] as string[],
                selectedTaskStatus: [] as number[],
                selectedEmployeesSearch: [] as IEmployeeFilterList[],
                startDate: null as string,
                reportFormat: "excel" as string
            },
            hosPalTaskStatus: HosPalTaskStatus,
            hCTaskStatus: HCTaskStatus,
            hHTaskStatus: HHTaskStatus,
            agencyMultiSelectOptions: {} as IMultiSelectDropDownOption,
            selectedAgenciesName: [] as string[],
            taskStatusOptions: {
                Completed: [
                    {
                        displayText: "Completed",
                        value: TaskReportStatus.Completed
                    },
                    {
                        value: TaskReportStatus.Submitted,
                        displayText: "Submitted"
                    },
                    {
                        value: TaskReportStatus.ExportReady,
                        displayText: "Export Ready"
                    },
                    {
                        value: TaskReportStatus.Exported,
                        displayText: "Exported"
                    },
                    {
                        value: TaskReportStatus.NotExported,
                        displayText: "Not Exported"
                    },
                    {
                        value: TaskReportStatus.SentToPhysician,
                        displayText: "Sent To Physician"
                    }
                ],
                Incomplete: [
                    {
                        value: TaskReportStatus.Saved,
                        displayText: "Saved"
                    },
                    {
                        value: TaskReportStatus.NotYetStarted,
                        displayText: "Not Yet Started"
                    },
                    {
                        value: TaskReportStatus.Returned,
                        displayText: "Returned"
                    },
                    {
                        value: TaskReportStatus.Reopened,
                        displayText: "Reopened"
                    }
                ],
                Missed: [
                    {
                        value: TaskReportStatus.MissedVisit,
                        displayText: "Missed Visit"
                    },
                    {
                        value: TaskReportStatus.MissedVisitComplete,
                        displayText: "Missed Visit (Complete)"
                    },
                    {
                        value: TaskReportStatus.MissedVisitPending,
                        displayText: "Missed Visit (Pending)"
                    },
                    {
                        value: TaskReportStatus.MissedVisitReturn,
                        displayText: "Missed Visit (Return)"
                    }
                ],
                Future: [
                    {
                        value: TaskReportStatus.NotYetDue,
                        displayText: "Not Yet Due"
                    }
                ]
            },
            allEmployeesList: [],
            allUsers: [] as IEmployeeFilterList[],
            patientList: [] as any,
            generating: false as boolean,
            v$: useVuelidate()
        };
    },

    validations() {
        return {
            patientScheduleDetails: {
                viewType: { required },
                startDate: { required }
            }
        };
    },

    created() {
        this._downloadService = Container.instance.get(DownloadService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._toastrService = Container.instance.get(ToastrService);
        this._taskService = Container.instance.get(TaskService);
        this._reportService = Container.instance.get(ReportService);
        this._analyticsService = Container.instance.get(AnalyticsService);
    },

    computed: {
        ...mapState(useApp, ["organizations"])
    },

    async mounted() {
        this.organizationId = window.localStorage.getItem(config.organizationId);
        this.agencyList = this.organizations.find(
            (organization: { id: string }) => organization.id === this.organizationId
        ).agencies;
        this.agencyList = this.agencyList.map((agency) => {
            const agencyAndApplicationIds = agency.id.split("_");
            let application = parseInt(agencyAndApplicationIds[1], 10);
            if (application == 256) {
                application = ApplicationEnum.AxxessHospice;
            }
            return {
                ...agency,
                application: application
            };
        });
        await this.setAgencyMultiSelect();
        this.setSelectedTaskStatus();
    },

    methods: {
        setAgencyMultiSelect() {
            try {
                this.agencyMultiSelectOptions = {};
                this.patientScheduleDetails.selectedAgencies.length = 0;
                if (!this.agencyList || this.agencyList?.length === 0) {
                    return;
                }
                const allowedProducts = [
                    ApplicationEnum.AgencyCore,
                    ApplicationEnum.HomeCare,
                    ApplicationEnum.AxxessHospice,
                    ApplicationEnum.AxxessPalliative
                ];
                const schedulerViewPermissions = this._permissionManager.getPermmission(
                    ParentPermissionEnum.scheduler,
                    PermissionActionEnum.canView
                );
                const schedulerAgencies = schedulerViewPermissions
                    .filter((item: any) => {
                        if (!item) {
                            return false;
                        }
                        let application = item.application;
                        if (application === ApplicationEnum.AxxessHospiceFE) {
                            application = ApplicationEnum.AxxessHospice;
                        }
                        return allowedProducts.includes(application);
                    })
                    .map((x: any) => x.agencyId);
                const filterableProducts = this.agencyList.filter((agency) => {
                    const isAllowed = allowedProducts.includes(agency.application);
                    const agencyId = agency.id?.split("_")[0];
                    return isAllowed && schedulerAgencies.includes(agencyId);
                });
                filterableProducts?.forEach((agency) => {
                    const appName = ApplicationNameEnum[agency.application];

                    if (!this.agencyMultiSelectOptions[appName]) {
                        this.agencyMultiSelectOptions[appName] = [];
                    }

                    this.agencyMultiSelectOptions[appName].push({
                        displayText: agency.name,
                        value: agency.id
                    });
                });

                Object.values(this.agencyMultiSelectOptions).forEach((agency: any) => {
                    const values = agency.map((item: any) => item.value);
                    this.patientScheduleDetails.selectedAgencies.push(...values);
                });
                // For select agencies change
            } catch (e) {
                console.warn(e);
                // Silent fail
            }
        },

        setSelectedTaskStatus() {
            Object.values(this.taskStatusOptions).forEach((status: any) => {
                const values = status.map((item: any) => item.value);
                this.patientScheduleDetails.selectedTaskStatus.push(...values);
            });
        },

        async updateBranches(branches: any) {
            this.patientScheduleDetails.selectedAgencies.length = 0;
            this.patientScheduleDetails.selectedAgencies.push(...branches);
            if (this.patientScheduleDetails.selectedEmployeesSearch.length) {
                this.patientScheduleDetails.selectedEmployeesSearch = [];
            }
            this.selectedAgenciesName = this.agencyList
                .filter((agency: any) => this.patientScheduleDetails.selectedAgencies.includes(agency.id))
                .map((agency: any) => agency.name);
        },

        updateTaskStatus(values: any) {
            this.patientScheduleDetails.selectedTaskStatus.length = 0;
            this.patientScheduleDetails.selectedTaskStatus.push(...values);
        },

        handleBranchesOnChange(branches: any) {
            this.patientScheduleDetails.selectedBranches.length = 0;
            this.patientScheduleDetails.selectedBranches.push(...branches);
        },

        handleDateChange(date: any) {
            this.patientScheduleDetails.startDate = date;
        },

        async fetchEmployeeList(filter: string, pageNumber: number = 1) {
            if (this.patientScheduleDetails.selectedAgencies.length > 0) {
                try {
                    let patientFilterOptions = {
                        pageLength: 15,
                        page: 1,
                        term: filter,
                        patientStatus: null,
                        agencies: this.patientScheduleDetails.selectedAgencies,
                        locationIds: this.patientScheduleDetails.selectedBranches,
                        scheduleFilter: 0,
                        startDate: this.patientScheduleDetails.startDate,
                        endDate:
                            this.patientScheduleDetails.viewType === "Weekly"
                                ? moment(this.patientScheduleDetails.startDate).add(6, "days").format("MM/DD/YYYY")
                                : this.patientScheduleDetails.startDate
                    } as IPatientCalendarRequestParams;
                    const data = await this._taskService.fetchTaskRangeBypatient(patientFilterOptions);
                    // if (data.Patient?.length) {
                    this.patientList = data?.Patient;

                    if ((filter && pageNumber > 1) || (!filter && pageNumber > 1)) {
                        this.patientList.map((user: any) => {
                            this.allUsers.push({
                                name: user.FirstName + " " + user.LastName,
                                value: user.Id,
                                agencies: user.AgencyId
                            });
                        });
                    } else if (filter && pageNumber === 1) {
                        this.allUsers = [] as IEmployeeFilterList[];
                        this.patientList.map((user: any) => {
                            this.allUsers.push({
                                name: user.FirstName + " " + user.LastName,
                                value: user.Id,
                                agencies: user.AgencyId
                            });
                        });
                    } else {
                        this.allUsers = [];
                        this.patientList.map((user: any) => {
                            this.allUsers.push({
                                name: user.FirstName + " " + user.LastName,
                                value: user.Id,
                                agencies: user.AgencyId
                            });
                        });
                    }
                    return this.allUsers;
                    // }
                } catch (err) {
                    this._toastrService.error({
                        title: `Error`,
                        message: `Failed to fetch patient list.`
                    });
                }
            }
            return [];
        },
        selectedEmployeesSearchUpdated(options: IEmployeeFilterList[]) {
            this.patientScheduleDetails.selectedEmployeesSearch = options;
        },
        async generateReport() {
            if (this.patientScheduleDetails.selectedAgencies.length) {
                const isValidated = await this.v$.$validate();
                if (isValidated) {
                    this.generating = true;
                    const isExcel = this.patientScheduleDetails.reportFormat === "excel";
                    this._analyticsService.logEvent({
                        category: AnalyticsCategory.PatientScheduleReport,
                        action: isExcel ? "Excel Export" : "PDF Export"
                    });
                    // fetching employees
                    let patientIds = [] as any;
                    if (this.patientScheduleDetails.selectedEmployeesSearch.length) {
                        this.patientScheduleDetails.selectedEmployeesSearch.forEach((user: any) => {
                            patientIds.push(user.value);
                        });
                    }
                    const allApplicationsSelected = [
                        ...new Set(
                            this.patientScheduleDetails.selectedAgencies.map((agency) => {
                                let application = Number(agency.split("_")[1]);
                                return application;
                            })
                        )
                    ];
                    // creating status filters for each application
                    let statusFilters = [] as any;
                    let isMissed: boolean;
                    allApplicationsSelected.forEach((application: number) => {
                        let allStatuses = [] as number[];
                        this.patientScheduleDetails.selectedTaskStatus.forEach((status: number) => {
                            allStatuses = [...allStatuses, ...getTaskStatus(application, status)];
                            isMissed = allStatuses.includes(17);
                        });

                        let allStatusesName = [];
                        allStatuses.forEach((st: number) => {
                            allStatusesName.push(this.statusName(st, application));
                        });

                        statusFilters.push({
                            Application: application,
                            StatusFilters: allStatusesName,
                            StatusFiltersValue: allStatuses,
                            isMissed: application == 256 || application == 65536 ? isMissed : false
                        });
                    });
                    const payload = {
                        LocationIds: [...this.patientScheduleDetails.selectedBranches],
                        AgencyIds: [
                            ...this.patientScheduleDetails.selectedAgencies.map((agency) => agency.split("_")[0])
                        ],
                        StartDate: this.patientScheduleDetails.startDate,
                        EndDate:
                            this.patientScheduleDetails.viewType === "Weekly"
                                ? moment(this.patientScheduleDetails.startDate).add(6, "days").format("MM/DD/YYYY")
                                : this.patientScheduleDetails.startDate,
                        PatientIds: patientIds,
                        StatusFilterList: statusFilters,
                        ReportType: this.patientScheduleDetails.reportFormat
                    };
                    let response: any;
                    if (this.patientScheduleDetails.reportFormat === "excel") {
                        response = await this._reportService.patientCalendarExport(payload);
                    } else {
                        response = await this._reportService.patientCalendarPdfExport(payload);
                    }
                    this._downloadService.downloadFile(response, response.type);
                    this.generating = false;
                    this._toastrService.success({
                        title: `Report Generated`,
                        message: `Report Generated Successfully!!`
                    });
                } else {
                    this._toastrService.error({
                        title: `Validation Error`,
                        message: `Please fill required details`
                    });
                }
            } else {
                this._toastrService.error({
                    title: `Error - Downloading Schedule`,
                    message: `Please select agency and try again.`
                });
            }
        },
        clearSelection() {
            this.patientScheduleDetails = {
                viewType: null as string,
                selectedBranches: [] as string[],
                selectedAgencies: [] as string[],
                selectedTaskStatus: [] as number[],
                selectedEmployeesSearch: [] as IEmployeeFilterList[],
                startDate: null as string,
                reportFormat: "excel" as string
            };
            this.updateBranches([]);
        },

        statusName(val: number, application: ApplicationEnum): string {
            let st;
            if (application == ApplicationEnum.AgencyCore) {
                st = this.hHTaskStatus.getDescriptionUsingValue(val);
            } else if (application == ApplicationEnum.HomeCare) {
                st = this.hCTaskStatus.getDescriptionUsingValue(val);
            } else {
                st = this.hosPalTaskStatus.getDescriptionUsingValue(val);
            }
            return st;
        },
        goToReportCenter() {
            this.$router.push({ name: "report-center" });
        }
    }
});
